import "../category.scss";
import "./accessories.scss";


import { PageProps, graphql } from "gatsby";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { findLast, uniqBy } from "lodash";

import CategoryFilters from "./CategoryFilters";
import { LocalType } from "../../models/localType";
import { SEOArguments } from "../../classes/seoArguments";
import { useTranslation } from "react-i18next";
import { AccessoriesListing } from "../../components/productListing";
import SEO from "../../components/Seo";
import { AccessoriesTemplateContext } from "../../models/context/accessoriesTemplateContext";
import MainLayout from "../../components/layout/MainLayout";
import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services";
import { AccessoriesFilteredProduct } from "../../models/accessoriesFilteredProduct";
import LoadingFont from "../../components/primitives/LoadingFont";


export const query = graphql`
    query AccessoriesTemplate($category: String!) {
        accessories: allProductsJson(filter: { category: { regex: $category } }) {
            nodes {
                id
                code
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                images
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {
                    custom_made {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadataAccessories: allProductsJson(filter: { code: { eq: "century_accessoires" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadataVenting: allProductsJson(filter: { code: { eq: "century_tuyaux" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Accessories({
    data,
    pageContext: { locale, category_name },
}: PageProps<Queries.AccessoriesTemplateQuery, AccessoriesTemplateContext>) {
    const products = data.accessories.nodes;

    const { t } = useTranslation("templatePages");
    const [categoryFilter, setCategoryFilter] = useState<any[]>([]);
    const [search, setSearch] = useState<string>("");
    const [isLoading, setLoading] = useState<boolean>(true);
    const [filteredProducts, setFilteredProducts] = useState<AccessoriesFilteredProduct[]>([]);

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const currentLocale = locale as keyof LocalType;

    let allCategories = products.map((product: any) => {
        let categoryPath = String(product.category_localized?.[currentLocale]).split("/");
        let category = {
            id: product.category,
            name: findLast(categoryPath, (x) => x.length > 0),
            type: categoryPath?.[3],
        };
        return category;
    });

    let categories = uniqBy(allCategories, "id").sort((a: any, b: any) => (a.id > b.id ? 1 : -1));

    useEffect(() => {
        const filterProducts = () => {
            //setFilteredProducts(
            let newFilteredProducts = products.filter(
                (product: any) =>
                    (categoryFilter.includes(product?.category as string) ||
                        categoryFilter.length === 0) &&
                    ((product?.title?.[currentLocale] &&
                        String(product?.title?.[currentLocale])
                            .toLowerCase()
                            .includes(search.toLowerCase())) ||
                        String(product?.code).toLowerCase().includes(search.toLowerCase()))
            );
            servicesContext?.magento.productService.operations
                .getProducts(newFilteredProducts.map((product: any) => product.code))
                .then((productsLive) => {
                    const newFilteredProductsAcc: AccessoriesFilteredProduct[] = [];
                    for (const product of newFilteredProducts) {
                        const productLive = productsLive.find(
                            (productLive: any) => productLive.sku === product.code
                        );
                        if (productLive) {
                            newFilteredProductsAcc.push({
                                product: product,
                                currentStock: productLive.currentStock,
                                currentPrice: productLive.currentPrice,
                            });
                        } else {
                            newFilteredProductsAcc.push({
                                product: product,
                                currentStock: {
                                    sbiStockCode: "",
                                    status: "out-of-stock",
                                    quantity: 0,
                                    availabilityDate: "",
                                    instock: false,
                                    specialPriceEndDate: "",
                                    specialPriceFromDate: "",
                                },
                                currentPrice: {
                                    regularPrice: { value: 0, currency: "CAD" },
                                    finalPrice: { value: 0, currency: "CAD" },
                                    discount: {
                                        percentOff: 0,
                                        amountOff: 0,
                                    },
                                },
                            });
                        }
                        setFilteredProducts(newFilteredProductsAcc);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
            //);
        };

        filterProducts();

        const interval = setInterval(() => {
            filterProducts();
        }, 5000);

        return () => clearInterval(interval);
    }, [products, search, categoryFilter, currentLocale]);

    const seoArgumentsAccessories = new SEOArguments(data.metadataAccessories, currentLocale);

    const seoArgumentsVenting = new SEOArguments(data.metadataVenting, currentLocale);

    return (
        <div className="category">
            <MainLayout>
                {category_name?.["en_CA"] === "Accessories" ? (
                    <SEO
                        title={seoArgumentsAccessories.title}
                        description={seoArgumentsAccessories.description}
                    />
                ) : (
                    <SEO
                        title={seoArgumentsVenting.title}
                        description={seoArgumentsVenting.description}
                    />
                )}
                <div className="container">
                    <div className="row categoryRow">
                        <aside id="secondary">
                            <CategoryFilters
                                lang={locale}
                                categories={categories}
                                categoryFilter={categoryFilter}
                                setCategoryFilter={setCategoryFilter}
                                search={search}
                                setSearch={setSearch}
                                template={category_name?.["en_CA"]}
                            />
                        </aside>

                        <div id="primary" className="accessories-list-container">
                            <header>
                                <div className="container">
                                    <h1>{category_name?.[currentLocale] ?? ""}</h1>
                                </div>
                            </header>
                            {isLoading ? (
                                <LoadingFont />
                            ) : (
                                <AccessoriesListing
                                    categories={categories}
                                    products={filteredProducts}
                                    template={category_name?.["en_CA"]}
                                    columns={[]}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </MainLayout>
        </div>
    );
}
